import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Breadcrumbs,
  Link,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  FileDownload as FileDownloadIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import type { RFQ, RFQStatus, RFQResponse, RFQResponseItem } from '../types';
import { rfqService } from '../services/rfqService';
import { colors } from '../theme';

// ============================================================================
// Configuración
// ============================================================================

const rfqStatusColors: Record<RFQStatus, string> = {
  draft: '#9e9e9e',
  sent: '#2196f3',
  responses: '#4caf50',
  closed: '#616161',
};

const rfqStatusLabels: Record<RFQStatus, string> = {
  draft: 'Borrador',
  sent: 'Enviada',
  responses: 'Respondida',
  closed: 'Cerrada',
};

// ============================================================================
// Helpers
// ============================================================================

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDateShort = (dateStr?: string): string => {
  if (!dateStr) return '—';
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: es });
  } catch {
    return '—';
  }
};

// ============================================================================
// Analysis helpers
// ============================================================================

interface ItemAnalysis {
  rfqItemId: string;
  minPrice: number;
  maxPrice: number;
  minPriceSupplierId: string;
  maxPriceSupplierId: string;
}

function analyzeItems(responses: RFQResponse[]): Map<string, ItemAnalysis> {
  const map = new Map<string, ItemAnalysis>();

  for (const response of responses) {
    for (const item of response.items) {
      const existing = map.get(item.rfqItemId);
      if (!existing) {
        map.set(item.rfqItemId, {
          rfqItemId: item.rfqItemId,
          minPrice: item.totalPrice,
          maxPrice: item.totalPrice,
          minPriceSupplierId: response.supplierId,
          maxPriceSupplierId: response.supplierId,
        });
      } else {
        if (item.totalPrice < existing.minPrice) {
          existing.minPrice = item.totalPrice;
          existing.minPriceSupplierId = response.supplierId;
        }
        if (item.totalPrice > existing.maxPrice) {
          existing.maxPrice = item.totalPrice;
          existing.maxPriceSupplierId = response.supplierId;
        }
      }
    }
  }

  return map;
}

function getBestTotalSupplierId(responses: RFQResponse[]): string | null {
  if (responses.length < 2) return null;
  const sorted = [...responses].sort((a, b) => a.totalAmountUSD - b.totalAmountUSD);
  return sorted[0].supplierId;
}

function getBestDeliverySupplierId(responses: RFQResponse[]): string | null {
  if (responses.length < 2) return null;
  const sorted = [...responses].sort((a, b) => a.deliveryDays - b.deliveryDays);
  return sorted[0].supplierId;
}

// ============================================================================
// Main Component
// ============================================================================

const RFQCompare: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [rfq, setRfq] = useState<RFQ | null>(null);
  const [loading, setLoading] = useState(true);

  // =========================================================================
  // Load data
  // =========================================================================

  useEffect(() => {
    if (!id) {
      navigate('/rfq');
      return;
    }

    const found = rfqService.getById(id);
    if (!found) {
      navigate('/rfq');
      return;
    }
    setRfq(found);
    setLoading(false);
  }, [id, navigate]);

  // =========================================================================
  // Computed analysis
  // =========================================================================

  const itemAnalysis = useMemo(() => {
    if (!rfq) return new Map<string, ItemAnalysis>();
    return analyzeItems(rfq.responses);
  }, [rfq]);

  const bestTotalId = useMemo(() => {
    if (!rfq) return null;
    return getBestTotalSupplierId(rfq.responses);
  }, [rfq]);

  const bestDeliveryId = useMemo(() => {
    if (!rfq) return null;
    return getBestDeliverySupplierId(rfq.responses);
  }, [rfq]);

  // =========================================================================
  // Get response item for a given rfqItemId and response
  // =========================================================================

  const getResponseItem = (response: RFQResponse, rfqItemId: string): RFQResponseItem | undefined => {
    return response.items.find(i => i.rfqItemId === rfqItemId);
  };

  // =========================================================================
  // Highlight helpers
  // =========================================================================

  const getCellBg = (rfqItemId: string, supplierId: string): string => {
    const analysis = itemAnalysis.get(rfqItemId);
    if (!analysis) return 'transparent';
    // Only highlight when there's actual price difference
    if (analysis.minPrice === analysis.maxPrice) return 'transparent';
    if (analysis.minPriceSupplierId === supplierId) return 'rgba(76, 175, 80, 0.1)';
    if (analysis.maxPriceSupplierId === supplierId) return 'rgba(244, 67, 54, 0.08)';
    return 'transparent';
  };

  const isBestItemPrice = (rfqItemId: string, supplierId: string): boolean => {
    const analysis = itemAnalysis.get(rfqItemId);
    if (!analysis) return false;
    if (analysis.minPrice === analysis.maxPrice) return false;
    return analysis.minPriceSupplierId === supplierId;
  };

  // =========================================================================
  // Excel export
  // =========================================================================

  const handleExportExcel = () => {
    if (!rfq) return;

    const responses = rfq.responses;
    const supplierNames = responses.map(r => r.supplierName);

    // Build rows
    const rows: Record<string, string | number>[] = [];

    // Header info
    rows.push({
      Item: `Comparación de Cotizaciones — ${rfq.number}`,
      Detalle: '',
      ...Object.fromEntries(supplierNames.map(name => [name, ''])),
    });
    rows.push({
      Item: `Título: ${rfq.title}`,
      Detalle: '',
      ...Object.fromEntries(supplierNames.map(name => [name, ''])),
    });
    rows.push({
      Item: '',
      Detalle: '',
      ...Object.fromEntries(supplierNames.map(name => [name, ''])),
    });

    // Item rows
    for (const item of rfq.items) {
      const row: Record<string, string | number> = {
        Item: item.category,
        Detalle: `${item.quantity} ${item.unit}${item.description ? ' - ' + item.description : ''}`,
      };

      for (const response of responses) {
        const respItem = getResponseItem(response, item.id);
        if (respItem) {
          const parts: string[] = [];
          parts.push(`USD ${respItem.unitPrice.toFixed(2)}/u`);
          parts.push(`Total: USD ${respItem.totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
          if (respItem.brand) parts.push(`Marca: ${respItem.brand}`);
          if (respItem.origin) parts.push(`Origen: ${respItem.origin}`);
          if (respItem.notes) parts.push(`Nota: ${respItem.notes}`);
          row[response.supplierName] = parts.join('\n');
        } else {
          row[response.supplierName] = 'Sin cotizar';
        }
      }

      rows.push(row);
    }

    // Separator
    rows.push({
      Item: '',
      Detalle: '',
      ...Object.fromEntries(supplierNames.map(name => [name, ''])),
    });

    // Summary rows
    const summaryFields: { label: string; getter: (r: RFQResponse) => string }[] = [
      { label: 'TOTAL GENERAL', getter: (r) => formatCurrency(r.totalAmountUSD) },
      { label: 'Plazo entrega', getter: (r) => `${r.deliveryDays} días` },
      { label: 'Cond. pago', getter: (r) => r.paymentTerms },
      { label: 'Garantía', getter: (r) => r.warranty || '—' },
      { label: 'Válida hasta', getter: (r) => formatDateShort(r.validUntil) },
    ];

    for (const field of summaryFields) {
      const row: Record<string, string | number> = {
        Item: field.label,
        Detalle: '',
      };
      for (const response of responses) {
        row[response.supplierName] = field.getter(response);
      }
      rows.push(row);
    }

    // Create workbook
    const ws = XLSX.utils.json_to_sheet(rows, { skipHeader: false });

    // Set column widths
    ws['!cols'] = [
      { wch: 25 },
      { wch: 30 },
      ...supplierNames.map(() => ({ wch: 35 })),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Comparación');

    const dateStr = format(new Date(), 'yyyyMMdd');
    XLSX.writeFile(wb, `Compulsa_${rfq.number}_${dateStr}.xlsx`);
  };

  // =========================================================================
  // Loading/Error states
  // =========================================================================

  if (loading || !rfq) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  // Empty state: less than 2 responses
  if (rfq.responses.length < 2) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/rfq')}>
            Compulsas & RFQ
          </Link>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rfq/${rfq.id}`)}>
            {rfq.number}
          </Link>
          <Typography color="text.primary">Comparación</Typography>
        </Breadcrumbs>

        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/rfq/${rfq.id}`)}>
              Volver al detalle
            </Button>
          }
          sx={{ mt: 2 }}
        >
          Se necesitan al menos 2 respuestas de proveedores para generar la comparación.
          Actualmente hay {rfq.responses.length} respuesta{rfq.responses.length !== 1 ? 's' : ''}.
        </Alert>
      </Box>
    );
  }

  const responses = rfq.responses;

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/rfq')}>
          Compulsas & RFQ
        </Link>
        <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rfq/${rfq.id}`)}>
          {rfq.number}
        </Link>
        <Typography color="text.primary">Comparación</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <div>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Comparación de Cotizaciones
              </Typography>
              <Chip
                label={rfqStatusLabels[rfq.status]}
                sx={{
                  bgcolor: rfqStatusColors[rfq.status],
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary">
              {rfq.number} — {rfq.title}
            </Typography>
          </div>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
          >
            Exportar a Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/rfq/${rfq.id}`)}
          >
            Volver al Detalle
          </Button>
        </Box>
      </Box>

      {/* Comparison Table */}
      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    bgcolor: '#f5f7fa',
                    minWidth: 180,
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                  }}
                >
                  Item
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    bgcolor: '#f5f7fa',
                    minWidth: 150,
                    position: 'sticky',
                    left: 180,
                    zIndex: 3,
                  }}
                >
                  Detalle
                </TableCell>
                {responses.map((response) => (
                  <TableCell
                    key={response.supplierId}
                    sx={{
                      fontWeight: 700,
                      bgcolor: '#f5f7fa',
                      minWidth: 220,
                      borderLeft: bestTotalId === response.supplierId
                        ? '4px solid #4caf50'
                        : '1px solid rgba(224, 224, 224, 1)',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {response.supplierName}
                    </Typography>
                    {bestTotalId === response.supplierId && (
                      <Chip
                        label="Mejor Total"
                        size="small"
                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem', height: 20, mt: 0.5 }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {/* Item rows */}
              {rfq.items.map((item, idx) => (
                <TableRow key={item.id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.02)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      position: 'sticky',
                      left: 0,
                      bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa',
                      zIndex: 1,
                      borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {item.category}
                    </Typography>
                    {item.specs && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {item.specs}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 180,
                      bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa',
                      zIndex: 1,
                      borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    <Typography variant="body2">
                      {item.quantity} {item.unit}
                    </Typography>
                    {item.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {item.description}
                      </Typography>
                    )}
                  </TableCell>
                  {responses.map((response) => {
                    const respItem = getResponseItem(response, item.id);
                    const cellBg = getCellBg(item.id, response.supplierId);
                    const isBest = isBestItemPrice(item.id, response.supplierId);

                    return (
                      <TableCell
                        key={response.supplierId}
                        sx={{
                          bgcolor: cellBg,
                          borderLeft: bestTotalId === response.supplierId
                            ? '4px solid #4caf50'
                            : '1px solid rgba(224, 224, 224, 1)',
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          textAlign: 'center',
                          verticalAlign: 'top',
                          py: 1.5,
                        }}
                      >
                        {respItem ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: isBest ? 700 : 400 }}
                            >
                              USD {respItem.unitPrice.toFixed(2)}/u
                              {isBest && (
                                <CheckIcon sx={{ fontSize: 14, color: '#4caf50', ml: 0.5, verticalAlign: 'middle' }} />
                              )}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isBest ? 700 : 500,
                                color: isBest ? '#2e7d32' : 'text.primary',
                                mt: 0.3,
                              }}
                            >
                              Total: {formatCurrency(respItem.totalPrice)}
                            </Typography>
                            {respItem.brand && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Marca: {respItem.brand}
                              </Typography>
                            )}
                            {respItem.origin && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Origen: {respItem.origin}
                              </Typography>
                            )}
                            {respItem.notes && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                                {respItem.notes}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            Sin cotizar
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {/* Separator row */}
              <TableRow>
                <TableCell
                  colSpan={2 + responses.length}
                  sx={{ bgcolor: '#e0e0e0', py: 0.5, borderBottom: '2px solid #bdbdbd' }}
                />
              </TableRow>

              {/* Summary: Total General */}
              <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    position: 'sticky',
                    left: 0,
                    bgcolor: '#f5f7fa',
                    zIndex: 1,
                  }}
                >
                  TOTAL GENERAL
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: 180, bgcolor: '#f5f7fa', zIndex: 1 }} />
                {responses.map((response) => (
                  <TableCell
                    key={`total-${response.supplierId}`}
                    sx={{
                      textAlign: 'center',
                      fontWeight: 700,
                      fontSize: '1rem',
                      bgcolor: bestTotalId === response.supplierId ? 'rgba(76, 175, 80, 0.15)' : '#f5f7fa',
                      color: bestTotalId === response.supplierId ? '#2e7d32' : 'text.primary',
                      borderLeft: bestTotalId === response.supplierId
                        ? '4px solid #4caf50'
                        : '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    {formatCurrency(response.totalAmountUSD)}
                    {bestTotalId === response.supplierId && (
                      <CheckIcon sx={{ fontSize: 16, color: '#4caf50', ml: 0.5, verticalAlign: 'middle' }} />
                    )}
                  </TableCell>
                ))}
              </TableRow>

              {/* Summary: Plazo de entrega */}
              <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    bgcolor: '#f5f7fa',
                    zIndex: 1,
                  }}
                >
                  Plazo entrega
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: 180, bgcolor: '#f5f7fa', zIndex: 1 }} />
                {responses.map((response) => (
                  <TableCell
                    key={`delivery-${response.supplierId}`}
                    sx={{
                      textAlign: 'center',
                      fontWeight: bestDeliveryId === response.supplierId ? 700 : 400,
                      color: bestDeliveryId === response.supplierId ? '#1565c0' : 'text.primary',
                      bgcolor: '#f5f7fa',
                      borderLeft: bestTotalId === response.supplierId
                        ? '4px solid #4caf50'
                        : '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    {response.deliveryDays} días
                    {bestDeliveryId === response.supplierId && (
                      <CheckIcon sx={{ fontSize: 14, color: '#1565c0', ml: 0.5, verticalAlign: 'middle' }} />
                    )}
                  </TableCell>
                ))}
              </TableRow>

              {/* Summary: Condiciones de pago */}
              <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    bgcolor: '#f5f7fa',
                    zIndex: 1,
                  }}
                >
                  Cond. pago
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: 180, bgcolor: '#f5f7fa', zIndex: 1 }} />
                {responses.map((response) => (
                  <TableCell
                    key={`payment-${response.supplierId}`}
                    sx={{
                      textAlign: 'center',
                      bgcolor: '#f5f7fa',
                      borderLeft: bestTotalId === response.supplierId
                        ? '4px solid #4caf50'
                        : '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    {response.paymentTerms}
                  </TableCell>
                ))}
              </TableRow>

              {/* Summary: Garantía */}
              <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    bgcolor: '#f5f7fa',
                    zIndex: 1,
                  }}
                >
                  Garantía
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: 180, bgcolor: '#f5f7fa', zIndex: 1 }} />
                {responses.map((response) => (
                  <TableCell
                    key={`warranty-${response.supplierId}`}
                    sx={{
                      textAlign: 'center',
                      bgcolor: '#f5f7fa',
                      borderLeft: bestTotalId === response.supplierId
                        ? '4px solid #4caf50'
                        : '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    {response.warranty || '—'}
                  </TableCell>
                ))}
              </TableRow>

              {/* Summary: Válida hasta */}
              <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    position: 'sticky',
                    left: 0,
                    bgcolor: '#f5f7fa',
                    zIndex: 1,
                  }}
                >
                  Válida hasta
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: 180, bgcolor: '#f5f7fa', zIndex: 1 }} />
                {responses.map((response) => (
                  <TableCell
                    key={`valid-${response.supplierId}`}
                    sx={{
                      textAlign: 'center',
                      bgcolor: '#f5f7fa',
                      borderLeft: bestTotalId === response.supplierId
                        ? '4px solid #4caf50'
                        : '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    {formatDateShort(response.validUntil)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Supplier Notes Section */}
      {responses.some(r => r.notes) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Observaciones de Proveedores
          </Typography>
          <Grid container spacing={2}>
            {responses
              .filter(r => r.notes)
              .map((response) => (
                <Grid item xs={12} sm={6} md={4} key={`note-${response.supplierId}`}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        {response.supplierName}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        "{response.notes}"
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default RFQCompare;
